import PaginationMui from "@app/components/pagination-mui/pagination-mui.component";
import { DEFAULT_PAGE, OrderType } from "@app/constants";
import CardReceiveIcon from "@app/icons/dashboard/card-receive";
import { DashboardAuthorService } from "@app/services/http/dashboard.service";
import { Meta, StatisticsState } from "@app/types";
import {
  BookRevenue as BookRevenueType,
  RequestParamsDashboard,
} from "@app/types/dashboard.type";
import { Images } from "@assets/images";
import clsx from "clsx";
import { memo, useEffect, useState } from "react";
import * as XLSX from "xlsx";

const headerTitle = [
  {
    label: "ID",
    key: "bookId",
    hasOrder: true,
  },
  {
    label: "TÊN SÁCH",
    key: "bookName",
    hasOrder: true,
  },
  {
    label: "LƯỢT XEM",
    key: "listens",
    hasOrder: true,
  },
  {
    label: "LƯỢT XEM (all)",
    key: "listensAllTime",
    hasOrder: false,
  },
  {
    label: "SỐ TIỀN THU ĐƯỢC",
    key: "money",
    hasOrder: true,
  },
];

const BookRevenueVip = ({ filter }: { filter: StatisticsState }) => {
  const [data, setData] = useState<BookRevenueType>();
  const [meta, setMeta] = useState<Meta>();
  const [requestParams, setRequestParams] = useState<RequestParamsDashboard>({
    pageNum: DEFAULT_PAGE,
    pageSize: 20,
  });
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("");

  useEffect(() => {
    DashboardAuthorService.getBookRevenueVip({
      ...requestParams,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
    }).then((res) => {
      setData(res.data);
      setMeta(res.data.meta);
    });
  }, [requestParams, filter.startDate, filter.endDate]);

  useEffect(() => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      sortBy: orderBy,
      sortType: order as OrderType,
    };

    setRequestParams(newRequestParams);
  }, [orderBy, order]);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      pageNum: page,
    };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      pageSize,
    };

    setRequestParams(newRequestParams);
  };

  const handleExportToExcel = () => {
    if (!data?.items) return;

    const excelData = [
      ["ID", "TÊN SÁCH", "LƯỢT XEM", "LƯỢT XEM (all)", "SỐ TIỀN THU ĐƯỢC"],
      ...((data?.items ?? []).map((item) => [
        item.bookId,
        item.bookName,
        item.listens ?? 0,
        item.listensAllTime ?? 0,
        `${(item.money ?? 0).toLocaleString("de-DE")}đ`,
      ])),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Danh sách");

    XLSX.writeFile(workbook, "Sach_VIP_cua_toi.xlsx");
  };

  const handleClickHeader = (item: any) => {
    if (item.hasOrder) {
      const orderBy = item.key;
      const orderType =
        order === OrderType.ASC ? OrderType.DESC : OrderType.ASC;

      setOrderBy(orderBy);
      setOrder(orderType);
    }
  };

  return (
    <div className="p-[24px] bg-white rounded-xl flex flex-col gap-[20px]">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex gap-1 items-center">
            <CardReceiveIcon />
            <p className="text-[18px] text-[#45464E] font-medium">
              Doanh thu sách VIP của tôi
            </p>
          </div>
          <div className="flex gap-6 items-end">
            <div>
              <p className="text-[14px] text-[#666]">Trong khoảng lọc</p>
              <p className="text-[28px] text-[#1C1D22] font-medium">
                {data?.totalMoneyAuthor
                  ? data?.totalMoneyAuthor.toLocaleString("de-DE")
                  : 0}
                đ
              </p>
            </div>
            <div>
              <p className="text-[14px] text-[#666]">Tổng tích lũy</p>
              <p className="text-[20px] text-[#1C1D22]">
                {data?.totalMoneyAllTime
                  ? data?.totalMoneyAllTime.toLocaleString("de-DE")
                  : 0}
                đ
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          disabled={!data?.items?.length}
          className="focus:outline-none text-white bg-green-700 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 disabled:opacity-50"
          onClick={handleExportToExcel}
        >
          Export Excel
        </button>
      </div>

      <div className="relative overflow-x-auto">
        <table className="w-full text-left rtl:text-right">
          <thead>
            <tr className="border-t border-[#EEF0FA]">
              {headerTitle?.map((it) => (
                <th
                  key={it.key}
                  className={clsx(
                    "px-6 py-3 text-[14px] text-[#6E7079] font-normal cursor-pointer",
                    {
                      "bg-[#bac5d4]": it.key === orderBy,
                    }
                  )}
                  onClick={() => handleClickHeader(it)}
                >
                  <div className={"flex items-center"}>
                    {it.label}

                    {it.hasOrder && (!order || it.key !== orderBy) && (
                      <span className="ml-2">
                        <img
                          src={Images.SortIcon.default}
                          alt="sort-icon"
                          className="w-4"
                        />
                      </span>
                    )}

                    {it.hasOrder &&
                      order === OrderType.ASC &&
                      orderBy === it.key && (
                      <div className="ml-2">
                        <img
                          src={Images.ArrowUpIcon.default}
                          alt="arrow-up-icon"
                          className="w-4"
                        />
                      </div>
                    )}

                    {it.hasOrder &&
                      order === OrderType.DESC &&
                      orderBy === it.key && (
                      <div className="ml-2">
                        <img
                          src={Images.ArrowDownIcon.default}
                          alt="arrow-down-icon"
                          className="w-4"
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.items.map((item, index) => (
              <tr
                key={index}
                className="border-t border-[#EEF0FA] text-[14px] text-[#45464E] font-normal"
              >
                <td className="px-6 py-4">{item.bookId}</td>
                <td className="px-6 py-4 flex items-center gap-1">
                  {item.thumbnails?.[0] && (
                    <img
                      src={item.thumbnails[0]}
                      alt="book image"
                      className="w-[32px] h-[32px] rounded-[5px]"
                    />
                  )}
                  <p>{item.bookName}</p>
                </td>
                <td className="px-6 py-4">{item.listens ?? 0}</td>
                <td className="px-6 py-4">{item.listensAllTime ?? 0}</td>
                <td className="px-6 py-4">
                  {item.money.toLocaleString("de-DE")}đ
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationMui
        currentPage={meta?.pageNum ?? 1}
        totalPages={meta?.totalPage ?? 1}
        sizePage={meta?.pageSize ?? 1}
        total={meta?.total ?? 0}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};

export default memo(BookRevenueVip);
